/**
 * AEOOnboardingWizard
 *
 * Thin router that shows an offering-type selector (Step 1), then delegates
 * to the correct wizard path based on the site's offering_type.
 *
 * - 'product' (default) → ProductAEOOnboardingWizard (original 12-step flow)
 * - 'service'           → ServiceAEOOnboardingWizard (4-step service flow)
 * - 'mixed'             → ServiceAEOOnboardingWizard (service path first, product later)
 */

import { useState, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import type { OfferingType } from '@/types/offering';
import { ProductAEOOnboardingWizard } from './AEOOnboardingWizard.product';
import { ServiceAEOOnboardingWizard } from './AEOOnboardingWizard.service';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  ShoppingBag,
  Briefcase,
  Layers,
  Wand2,
  Loader2
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

const OFFERING_OPTIONS: { value: OfferingType; label: string; description: string; icon: typeof ShoppingBag }[] = [
  {
    value: 'product',
    label: 'Products',
    description: 'Physical or digital products with SKUs, prices, and specifications',
    icon: ShoppingBag,
  },
  {
    value: 'service',
    label: 'Services',
    description: 'Experiences, consultations, tours, or professional services',
    icon: Briefcase,
  },
  {
    value: 'mixed',
    label: 'Both',
    description: 'Your site offers both products and services',
    icon: Layers,
  },
];

export default function AEOOnboardingWizard() {
  const { selectedSite, setSelectedSite } = useSite();

  const [isSelecting, setIsSelecting] = useState(false);
  const [selected, setSelected] = useState<OfferingType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleOfferingSelect = useCallback(async (type: OfferingType) => {
    if (!selectedSite?.id || isSelecting) return;

    setSelected(type);
    setIsSelecting(true);
    setError(null);

    try {
      const res = await authFetch(`/api/sites/${selectedSite.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offering_type: type }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update offering type');
      }

      // Update site context so useOfferingType picks up the new value
      setSelectedSite({ ...selectedSite, offering_type: type });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save selection');
      setSelected(null);
    } finally {
      setIsSelecting(false);
    }
  }, [selectedSite, isSelecting, setSelectedSite]);

  // No site selected
  if (!selectedSite) {
    return (
      <div className="container mx-auto py-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the site selector to start the AEO onboarding wizard.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // If offering_type is already set, delegate to the right wizard
  if (selectedSite.offering_type) {
    switch (selectedSite.offering_type) {
      case 'service':
      case 'mixed':
        return <ServiceAEOOnboardingWizard />;
      case 'product':
      default:
        return <ProductAEOOnboardingWizard />;
    }
  }

  // Step 1: Offering type selection
  return (
    <div className="container mx-auto py-6 space-y-6">
      <h1 className="text-3xl font-bold">AEO Onboarding Wizard</h1>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="h-6 w-6" />
            What does your site offer?
          </CardTitle>
          <CardDescription>
            This determines which onboarding path we'll guide you through.
            You can change this later in site settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4">
            {OFFERING_OPTIONS.map(({ value, label, description, icon: Icon }) => (
              <Card
                key={value}
                className={`cursor-pointer transition-all hover:border-primary hover:shadow-md ${
                  selected === value ? 'border-primary ring-1 ring-primary bg-primary/5' : ''
                } ${isSelecting ? 'pointer-events-none opacity-60' : ''}`}
                onClick={() => handleOfferingSelect(value)}
              >
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-muted shrink-0">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{label}</p>
                    <p className="text-sm text-muted-foreground">{description}</p>
                  </div>
                  {isSelecting && selected === value && (
                    <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
